import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EditBotProfileModal from "./EditBotProfileModal";
import ArchivePersonaModal from "./ArchivePersonaModal";
import PersonaAssignmentsTab from "./PersonaAssignmentsTab";
import { BotProfile } from "./types";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useTrackEvent from "@/hooks/useTrackEvent";

// ─── Inline help content ─────────────────────────────────────────────────────

const HELP_SECTIONS = [
  {
    icon: "fa-robot",
    title: "What is a Bot Persona?",
    body: "A persona is the name and face your bot shows to visitors during a chat. Instead of a generic \"Bot\", your visitor sees \"Sales Bot\" or \"Support Assistant\" with a custom avatar."
  },
  {
    icon: "fa-star",
    title: "Default Persona",
    body: "Mark one persona as Default. When a workflow doesn't specify a persona, the default is used automatically. Every site should have exactly one default."
  },
  {
    icon: "fa-sitemap",
    title: "Assigning Personas to Workflows",
    body: "Open a workflow, find the Bot or AI node, and pick the persona to use. That workflow will always show that specific persona — Sales Bot for sales flows, Support Bot for support flows."
  },
  {
    icon: "fa-trash",
    title: "Deleting a Persona",
    body: "Personas that have been used in conversations cannot be deleted. Set a different persona as the default, or create a new one. If you delete the default persona, the next one is automatically promoted."
  }
];

// ─── Component ───────────────────────────────────────────────────────────────

type TabId = "personas" | "assignments";
type SortOption = "default-first" | "name-asc" | "name-desc";

export default function BotProfilesEditor() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const editIdParam = searchParams.get("editId");

  const [profiles, setProfiles] = useState<BotProfile[]>([]);
  const [idToEdit, setIdToEdit] = useState<null | number>(editIdParam ? parseInt(editIdParam, 10) : null);
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState<SortOption>("default-first");
  const [activeTab, setActiveTab] = useState<TabId>("personas");
  const [archiveTarget, setArchiveTarget] = useState<BotProfile | null>(null);
  const { displayToast } = useToastAlert();
  const { track } = useTrackEvent();

  const fetchProfiles = useCallback(async (includeArchived = false) => {
    try {
      const url = includeArchived ? "BotProfiles/list?includeArchived=true" : "BotProfiles/list";
      const response = await api.messaging.get(url);
      if (response.ok) {
        const data = await response.json();
        setProfiles(Array.isArray(data) ? data : []);
      }
    } catch {
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const doArchiveProfile = useCallback(
    async (profile: BotProfile) => {
      try {
        const result = await api.messaging.delete("BotProfiles", { id: profile.id });
        if (!result.ok) {
          const errorText = await result.text().catch(() => "");
          displayToast(Severity.Error, errorText || "Failed to archive persona.");
          return;
        }
        setProfiles((prev) => prev.filter((p) => p.id !== profile.id));
        displayToast(Severity.Success, `"${profile.name}" archived.`);
      } catch {
        displayToast(Severity.Error, "Failed to archive persona.");
      }
      setArchiveTarget(null);
    },
    [api.messaging, displayToast]
  );

  const duplicateProfile = useCallback(
    async (profile: BotProfile) => {
      try {
        const result = await api.messaging.post(`BotProfiles/${profile.id}/duplicate`, {});
        if (!result.ok) {
          displayToast(Severity.Error, "Failed to duplicate persona.");
          return;
        }
        const copy: BotProfile = await result.json();
        setProfiles((prev) => [...prev, copy]);
        displayToast(Severity.Success, `"${copy.name}" created.`);
      } catch {
        displayToast(Severity.Error, "Failed to duplicate persona.");
      }
    },
    [api.messaging, displayToast]
  );

  const unarchiveProfile = useCallback(
    async (profile: BotProfile) => {
      try {
        const result = await api.messaging.post("BotProfiles/unarchive", { id: profile.id });
        if (!result.ok) {
          displayToast(Severity.Error, "Failed to restore persona.");
          return;
        }
        setProfiles((prev) =>
          prev.map((p) => (p.id === profile.id ? { ...p, isArchived: false } : p))
        );
        displayToast(Severity.Success, `"${profile.name}" restored.`);
      } catch {
        displayToast(Severity.Error, "Failed to restore persona.");
      }
    },
    [api.messaging, displayToast]
  );

  const setDefault = useCallback(
    async (profile: BotProfile) => {
      try {
        const result = await api.messaging.post("BotProfiles/set-default", { id: profile.id });
        if (!result.ok) {
          displayToast(Severity.Error, "Failed to set default.");
          return;
        }
        setProfiles((prev) =>
          prev.map((p) => ({ ...p, isDefault: p.id === profile.id }))
        );
        displayToast(Severity.Success, `"${profile.name}" is now the default persona.`);
      } catch {
        displayToast(Severity.Error, "Failed to set default.");
      }
    },
    [api.messaging, displayToast]
  );

  useEffect(() => {
    fetchProfiles(showArchived);
  }, [fetchProfiles, showArchived]);

  const profileToEdit = profiles.find((p) => p.id === idToEdit);
  const defaultProfile = profiles.find((p) => p.isDefault && !p.isArchived) ?? profiles.find((p) => !p.isArchived);
  const activeProfiles = profiles.filter((p) => !p.isArchived);

  const visibleProfiles = profiles
    .filter((p) => {
      if (!showArchived && p.isArchived) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortOption === "default-first") {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      }
      const cmp = a.name.localeCompare(b.name);
      return sortOption === "name-desc" ? -cmp : cmp;
    });

  return (
    <div className="py-6 px-1">
      {/* ── Back button (when navigated from workflow editor) ── */}
      {returnTo && (
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          className="mb-4 flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:opacity-80 hover:underline"
        >
          <i className="fa fa-arrow-left text-xs" /> Back to Workflow
        </button>
      )}

      {/* ── Hero header ── */}
      <div className="rounded-2xl overflow-hidden mb-6 shadow-sm"
        style={{ background: "linear-gradient(135deg, #3B5D88 0%, #6E51A3 60%, #1670e7 100%)" }}>
        <div className="px-8 py-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
              <i className="fa fa-user-circle text-white text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Bot Personas</h2>
              <p className="text-sm text-white/75 leading-relaxed max-w-md">
                Create named personas for your bots — each with a custom avatar and name
                that visitors see in chat. Pin a persona to any workflow for a branded experience.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-colors flex items-center gap-1.5 backdrop-blur-sm"
              aria-label="Toggle help"
            >
              <i className={`fa ${showHelp ? "fa-times" : "fa-question-circle"}`} />
              {showHelp ? "Close" : "How it works"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/chatbots/new")}
              className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
            >
              <i className="fa fa-magic" />
              Build Your Bot
            </button>
            <button
              type="button"
              onClick={() => setIdToEdit(0)}
              className="px-4 py-2 rounded-lg bg-white text-primary-active text-xs font-bold hover:bg-slate-gray-50 transition-colors flex items-center gap-1.5 shadow-sm"
            >
              <i className="fa fa-plus" />
              New Persona
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="border-t border-white/10 px-8 py-3 flex items-center gap-6 bg-white/5">
          <StatPill icon="fa-users" label="Active" value={activeProfiles.length} />
          {defaultProfile && (
            <StatPill icon="fa-star" label="Default" value={defaultProfile.name} />
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {([["personas", "fa-user-circle", "Personas"], ["assignments", "fa-sitemap", "Workflow Assignments"]] as const).map(([tab, icon, label]) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab
                ? "border-primary text-primary-active"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            }`}
          >
            <i className={`fa ${icon} text-xs`} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Inline help panel ── */}
      {showHelp && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-5 animate-[fadeIn_0.15s_ease]">
          <div className="flex items-center gap-2 mb-4">
            <i className="fa fa-lightbulb-o text-[var(--color-primary)]" />
            <h3 className="text-sm font-bold text-slate-700">How Bot Personas Work</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {HELP_SECTIONS.map((s) => (
              <div key={s.title} className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                  <i className={`fa ${s.icon} text-[var(--color-primary)] text-sm`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-0.5">{s.title}</p>
                  <p className="text-xs text-primary-active leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab content ── */}
      {activeTab === "assignments" ? (
        <PersonaAssignmentsTab botProfiles={profiles} />
      ) : (
        <>
          {/* Toolbar: search + sort + filters */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[160px]">
              <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs" />
              <input
                type="text"
                placeholder="Search personas…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
              />
            </div>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              className="text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
            >
              <option value="default-first">Default first</option>
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
            </select>
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className={`text-xs px-3 py-2 rounded-lg border transition-colors whitespace-nowrap ${
                showArchived
                  ? "border-amber-300 bg-amber-50 text-amber-700"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
              }`}
            >
              <i className="fa fa-archive mr-1.5" />
              {showArchived ? "Hide archived" : "Archived"}
            </button>
          </div>

          {/* Profile grid */}
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="flex flex-col items-center gap-3 text-slate-gray-400">
                <i className="fa fa-circle-o-notch fa-spin text-2xl text-primary-hover" />
                <span className="text-sm">Loading personas…</span>
              </div>
            </div>
          ) : activeProfiles.length === 0 ? (
            <EmptyState onAdd={() => setIdToEdit(0)} />
          ) : (
            <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
              {visibleProfiles.map((p) => (
                <BotProfileCard
                  key={p.id}
                  profile={p}
                  isDefault={!!p.isDefault}
                  onEdit={() => !p.isArchived && setIdToEdit(p.id)}
                  onArchive={() => setArchiveTarget(p)}
                  onSetDefault={() => setDefault(p)}
                  onUnarchive={p.isArchived ? () => unarchiveProfile(p) : undefined}
                  onDuplicate={() => duplicateProfile(p)}
                />
              ))}

              {/* Add new card */}
              {!showArchived && !search && (
                <button
                  type="button"
                  onClick={() => setIdToEdit(0)}
                  className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-gray-300 hover:border-primary-hover hover:bg-primary-bg text-slate-gray-400 hover:text-primary transition-all group/add min-h-[200px]"
                  aria-label="Add new persona"
                >
                  <div className="w-14 h-14 rounded-full border-2 border-dashed border-current flex items-center justify-center group-hover/add:scale-110 transition-transform">
                    <i className="fa fa-plus text-xl" />
                  </div>
                  <span className="text-sm font-semibold">Add Persona</span>
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Archive confirmation modal ── */}
      {archiveTarget && (
        <ArchivePersonaModal
          profile={archiveTarget}
          onConfirm={() => doArchiveProfile(archiveTarget)}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {/* ── Edit modal ── */}
      {idToEdit != null && (
        <EditBotProfileModal
          key={idToEdit}
          botProfile={profileToEdit}
          onClose={() => setIdToEdit(null)}
          onSave={(profile) => {
            const wasDefault = profileToEdit?.isDefault === true;
            if (!profileToEdit) {
              setProfiles((prev) => {
                const updated = profile.isDefault
                  ? prev.map((x) => ({ ...x, isDefault: false }))
                  : [...prev];
                return [...updated, profile];
              });
            } else {
              setProfiles((prev) =>
                prev.map((x) =>
                  x.id === profile.id
                    ? profile
                    : profile.isDefault
                      ? { ...x, isDefault: false }
                      : x
                )
              );
            }
            // When a default profile is demoted, the backend promotes another.
            // Re-fetch to show the correct default badge.
            if (wasDefault && !profile.isDefault) {
              fetchProfiles(showArchived);
            }
            track("feature_click", "Bot/ProfileSaved");
            setIdToEdit(null);
            displayToast(Severity.Success, `"${profile.name}" saved.`);
          }}
        />
      )}
    </div>
  );
}

// ─── Stat pill ───────────────────────────────────────────────────────────────

function StatPill({ icon, label, value }: { icon: string; label: string; value: string | number }) {
  return (
    <div className="flex items-center gap-2 text-white/80">
      <i className={`fa ${icon} text-white/60 text-xs`} />
      <span className="text-xs text-white/60">{label}:</span>
      <span className="text-xs font-semibold text-white">{value}</span>
    </div>
  );
}

// ─── Profile card ─────────────────────────────────────────────────────────────

const TONE_EMOJI: Record<string, string> = {
  friendly: "😊",
  professional: "💼",
  empathetic: "💙",
  concise: "⚡",
};

const MODEL_SHORT: Record<string, string> = {
  // Anthropic Claude 4.x (current)
  "claude-haiku-4-5-20251001": "Haiku 4.5",
  "claude-sonnet-4-6": "Sonnet 4.6",
  "claude-opus-4-6": "Opus 4.6",
  // Azure OpenAI
  "velaro-gpt-4o-mini": "GPT-4o Mini",
  "velaro-gpt-4o": "GPT-4o",
  "velaro-gpt-5": "GPT-5",
  "velaro-gpt-5-mini": "GPT-5 Mini",
  // Google Gemini
  "gemini-2.0-flash": "Gemini Flash",
  "gemini-1.5-pro": "Gemini Pro",
  // Qwen
  "velaro-qwen-2-5-72b": "Qwen 2.5",
  // Legacy aliases — kept for DB display compat (model already resolved server-side)
  "claude-3-haiku-20240307": "Haiku (legacy)",
  "claude-3-5-haiku-20241022": "Haiku 3.5 (legacy)",
  "claude-3-5-sonnet-20240620": "Sonnet 3.5 (legacy)",
};

function BotProfileCard({
  profile,
  isDefault,
  onEdit,
  onArchive,
  onSetDefault,
  onUnarchive,
  onDuplicate,
}: {
  profile: BotProfile;
  isDefault: boolean;
  onEdit(): void;
  onArchive(): void;
  onSetDefault(): void;
  onUnarchive?: () => void;
  onDuplicate(): void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isArchived = !!profile.isArchived;

  return (
    <div className={`group relative bg-white rounded-2xl border transition-all overflow-hidden flex flex-col ${
      isArchived
        ? "border-slate-200 opacity-60 grayscale"
        : "border-slate-200 hover:border-[var(--color-primary)]/50 hover:shadow-md"
    }`}>
      {/* Archived badge */}
      {isArchived && (
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold border border-amber-200 z-10">
          Archived
        </div>
      )}
      {/* Avatar area */}
      <div
        className={`flex flex-col items-center pt-5 pb-3 px-4 flex-1 ${isArchived ? "cursor-default" : "cursor-pointer"}`}
        onClick={isArchived ? undefined : onEdit}
        role={isArchived ? undefined : "button"}
        tabIndex={isArchived ? undefined : 0}
        onKeyDown={(e) => { if (!isArchived && (e.key === "Enter" || e.key === " ")) onEdit(); }}
        aria-label={isArchived ? undefined : `Edit ${profile.name}`}
      >
        <div className="relative mb-3">
          <img
            src={profile.picture}
            alt={profile.name}
            className="w-16 h-16 rounded-full border-4 border-white shadow-md object-cover group-hover:scale-105 transition-transform"
          />
          {isDefault && (
            <span title="Default persona"
              className="absolute -top-1 -right-1 w-5 h-5 bg-sweet-orange-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
              <i className="fa fa-star text-white" style={{ fontSize: "8px" }} />
            </span>
          )}
          {profile.enableVoice && (
            <span title="Voice enabled"
              className="absolute -bottom-1 -right-1 w-5 h-5 bg-grape-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
              <i className="fa fa-microphone text-white" style={{ fontSize: "8px" }} />
            </span>
          )}
        </div>

        <p className="text-sm font-bold text-slate-gray-800 text-center truncate w-full">{profile.name}</p>

        {/* Chips — fixed area so all cards stay same height */}
        <div className="mt-3 flex flex-col items-center gap-1.5 min-h-[44px] w-full">
          <div className="flex flex-wrap gap-1 justify-center">
            {isDefault && (
              <span className="px-1.5 py-0.5 rounded-full bg-sweet-orange-50 text-sweet-orange-700 text-xs font-medium border border-sweet-orange-200">
                Default
              </span>
            )}
            {profile.tone && (
              <span className="px-1.5 py-0.5 rounded-full bg-slate-gray-100 text-slate-gray-600 text-xs">
                {TONE_EMOJI[profile.tone] || ""} {profile.tone}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 justify-center">
            {profile.aiModel && (
              <span className="px-1.5 py-0.5 rounded-full bg-primary-bg text-primary text-xs font-medium">
                {MODEL_SHORT[profile.aiModel] ?? profile.aiModel}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action bar — icon-only, visible on hover */}
      <div className="border-t border-slate-gray-100 bg-slate-gray-50 flex items-stretch opacity-0 group-hover:opacity-100 transition-opacity">
        {isArchived ? (
          <>
            <button type="button" onClick={(e) => { e.stopPropagation(); onUnarchive?.(); }}
              className="flex-1 py-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors flex items-center justify-center"
              title={`Restore ${profile.name}`}>
              <i className="fa fa-undo" />
            </button>
            <div className="w-px bg-slate-gray-200" />
            <button type="button" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="flex-1 py-2 text-slate-500 hover:text-primary hover:bg-primary-bg transition-colors flex items-center justify-center"
              title={`Duplicate ${profile.name}`}>
              <i className="fa fa-copy" />
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onEdit}
              className="flex-1 py-2 text-slate-gray-600 hover:text-primary hover:bg-primary-bg transition-colors flex items-center justify-center"
              title={`Edit ${profile.name}`}>
              <i className="fa fa-pencil" />
            </button>
            <div className="w-px bg-slate-gray-200" />
            <button type="button" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="flex-1 py-2 text-slate-500 hover:text-primary hover:bg-primary-bg transition-colors flex items-center justify-center"
              title={`Duplicate ${profile.name}`}>
              <i className="fa fa-copy" />
            </button>
            {!isDefault && (
              <>
                <div className="w-px bg-slate-gray-200" />
                <button type="button" onClick={(e) => { e.stopPropagation(); onSetDefault(); }}
                  className="flex-1 py-2 text-slate-gray-500 hover:text-sweet-orange-600 hover:bg-sweet-orange-50 transition-colors flex items-center justify-center"
                  title={`Set ${profile.name} as default`}>
                  <i className="fa fa-star" />
                </button>
              </>
            )}
            <div className="w-px bg-slate-gray-200" />
            <button type="button" onClick={(e) => { e.stopPropagation(); onArchive(); }}
              className="flex-1 py-2 text-slate-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors flex items-center justify-center"
              title={`Archive ${profile.name}`}>
              <i className="fa fa-archive" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({ onAdd }: { onAdd(): void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-bg to-grape-100 flex items-center justify-center mb-4 shadow-sm">
        <i className="fa fa-robot text-3xl text-primary-hover" />
      </div>
      <h3 className="text-base font-bold text-slate-gray-700 mb-2">No bot personas yet</h3>
      <p className="text-sm text-slate-gray-500 mb-6 max-w-xs leading-relaxed">
        Create your first bot persona to give your bot a name and face that visitors will recognize.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="px-5 py-2.5 rounded-lg bg-primary hover:bg-primary text-white text-sm font-semibold transition-colors flex items-center gap-2 shadow-sm"
      >
        <i className="fa fa-plus" />
        Create First Persona
      </button>
    </div>
  );
}
